import Config from '@/config'
import { exchangeLoginCodeForToken } from '@/services/auth'
import { useAuthStore } from '@/store'
import { AuthLoginState } from '@/store/auth/AuthenticationStore'
import { Header } from '@/ui/core/header'
import { ExternalLink, RouterLink } from '@/ui/core/link'
import { VStack } from '@/ui/core/stack'
import { Text } from '@/ui/core/typography'
import { router, useLocalSearchParams } from 'expo-router'
import { useRef } from 'react'
import { Platform, StyleSheet, type ViewStyle } from 'react-native'
import { OtpInput } from 'react-native-otp-entry'
import type { OtpInputRef } from 'react-native-otp-entry'
import Toast from 'react-native-toast-message'

const InvalidOtpCodeText =
	'Please ensure you have entered the correct code sent to your email and try again.'

const createOtpStyles = (isMobile: boolean) =>
	StyleSheet.create({
		containerStyle: {
			maxWidth: 320,
		},
		pinCode: {
			color: isMobile ? 'white' : 'black',
		},
		filledInCode: {
			borderColor: isMobile ? 'white' : 'black',
		},
		pinCodeContainer: {
			borderRadius: 0,
			borderColor: isMobile ? 'white' : 'black',
			borderWidth: 2,
			maxWidth: 60,
		},
	})

const SubmittedLoginForm = ({ isMobile }: { isMobile: boolean }) => {
	const isMobileDevice = Platform.OS === 'android' || Platform.OS === 'ios'
	const { setLoginState } = useAuthStore()
	const otpRef = useRef<OtpInputRef>()
	const otpStyles = createOtpStyles(isMobile)
	const textClass = isMobile ? 'text-white' : 'text-black'

	const { redirectTo } = useLocalSearchParams<{ redirectTo?: string }>()

	const resetSubmission = () => {
		setLoginState(AuthLoginState.PENDING)
	}

	const submitOtpCode = async (code: string) => {
		const onSuccess = () => {
			redirectTo ? router.navigate(redirectTo) : router.navigate('/index')
		}

		const onFailure = () => {
			otpRef?.current?.clear()

			Toast.show({
				type: 'error',
				text2: InvalidOtpCodeText,
			})
		}

		await exchangeLoginCodeForToken(code, onSuccess, onFailure)
	}

	return (
		<VStack gap={16}>
			<VStack gap={24} className="justify-center">
				<VStack className="grow justify-center items-center" gap={24}>
					<Header
						className={`max-w-72 text-center mb-3 font-primary-medium ${textClass}`}
						variant="h3"
					>
						Check your email and enter the provided code.
					</Header>

					<OtpInput
						//@ts-ignore
						ref={otpRef}
						numberOfDigits={6}
						focusColor={isMobile ? 'white' : 'black'}
						onFilled={submitOtpCode}
						theme={{
							containerStyle: otpStyles.containerStyle as ViewStyle,
							pinCodeContainerStyle: otpStyles.pinCodeContainer as ViewStyle,
							filledPinCodeContainerStyle: otpStyles.filledInCode as ViewStyle,
							pinCodeTextStyle: otpStyles.pinCode as ViewStyle,
						}}
					/>
					<Text
						className={`lg:font-primary-light text-xs text-center sm:text-pretty ${textClass}`}
					>
						Didn't get an email?{' '}
						<RouterLink
							className={`text-xs font-primary-demi ${textClass}`}
							onPress={resetSubmission}
							//@ts-ignore
							href={{
								pathname: '/login',
								params: {
									redirectTo,
								},
							}}
						>
							Go back
						</RouterLink>{' '}
						and make sure to use the same email you signed up with or head over to{' '}
						{isMobileDevice && (
							<RouterLink
								className={`text-xs font-primary-demi ${textClass}`}
								onPress={resetSubmission}
								href="/join"
							>
								app.carebywally.com/join
							</RouterLink>
						)}
						{!isMobileDevice && (
							<ExternalLink
								className={`text-xs font-primary-demi ${textClass}`}
								onPress={resetSubmission}
								href={Config.JOIN_URL}
							>
								app.carebywally.com/join
							</ExternalLink>
						)}{' '}
						to sign up.
					</Text>
				</VStack>
			</VStack>
		</VStack>
	)
}

export { SubmittedLoginForm }
